import {Application} from '../backend/interfaces/application';
import {Reminder} from '../backend/interfaces/reminder';
import {UserPublicInfo} from '../backend/responses/user-public-info';
import {Logger} from '../utils/managers/logger';

export class DataCacheManager {
  // Singleton Instance
  private static _instance: DataCacheManager;

  // Private
  private logger: Logger;
  public applications: {
    [id: string]: Application;
  };
  public pendingApplications: {
    [id: string]: string;
  };
  public applicationsWithError: {
    [id: string]: string;
  };
  public reminders: {
    [id: string]: Reminder;
  };
  public pendingReminders: {
    [id: string]: string;
  };
  public remindersWithError: {
    [id: string]: string;
  };
  public users: {
    [id: string]: UserPublicInfo;
  };
  public pendingUsers: {
    [id: string]: string;
  };
  public usersWithError: {
    [id: string]: string;
  };

  static get instance(): DataCacheManager {
    if (!this._instance) {
      this._instance = new DataCacheManager();
    }
    return this._instance;
  }

  // Constructor
  constructor() {
    // Create Log
    this.logger = new Logger('DataCacheManager');

    // Set variables
    this.applications = {};
    this.pendingApplications = {};
    this.applicationsWithError = {};
    this.reminders = {};
    this.pendingReminders = {};
    this.remindersWithError = {};
    this.users = {};
    this.pendingUsers = {};
    this.usersWithError = {};
  }

  // ----- Applications ----- //

  // Add application to cache
  public addApplication(id: string, application: Application): void {
    // Update
    this.applications[id] = application;
  }

  // Add application that is being fetched
  public addPendingApplication(id: string): void {
    // Update
    this.pendingApplications[id] = id;
  }

  // Remove application from being fetched
  public removePendingApplication(id: string): void {
    // New value
    const newPendingApplications = {...this.pendingApplications};

    // Delete
    delete newPendingApplications[id];

    // Update
    this.pendingApplications = {...newPendingApplications};
  }

  // Add application with error
  public addApplicationWithError(id: string): void {
    // Update
    this.applicationsWithError[id] = id;
  }

  // ----- Applications ----- //

  // ----- Users ----- //

  // Add user to cache
  public addUser(id: string, user: UserPublicInfo): void {
    // Update
    this.users[id] = user;
  }

  // Add user that is being fetched
  public addPendingUser(id: string): void {
    // Update
    this.pendingUsers[id] = id;
  }

  // Remove user from being fetched
  public removePendingUser(id: string): void {
    // New value
    const newPendingUsers = {...this.pendingUsers};

    // Delete
    delete newPendingUsers[id];

    // Update
    this.pendingUsers = {...newPendingUsers};
  }

  // Add user with error
  public addUserWithError(id: string): void {
    // Update
    this.usersWithError[id] = id;
  }

  // ----- Applications ----- //

  // ----- Reminders ----- //

  // Add reminder to cache
  public addReminder(id: string, reminder: Reminder): void {
    // Update
    this.reminders[id] = reminder;
  }

  // Add reminder that is being fetched
  public addPendingReminder(id: string): void {
    // Update
    this.pendingReminders[id] = id;
  }

  // Remove reminder from being fetched
  public removePendingReminder(id: string): void {
    // New value
    const newPendingReminders = {...this.pendingReminders};

    // Delete
    delete newPendingReminders[id];

    // Update
    this.pendingReminders = {...newPendingReminders};
  }

  // Add reminder with error
  public addReminderWithError(id: string): void {
    // Update
    this.remindersWithError[id] = id;
  }

  // ----- Reminders ----- //

  // Clear everything
  public clearAll(): void {
    // Update
    this.applications = {};
    this.pendingApplications = {};
    this.applicationsWithError = {};
    this.users = {};
    this.pendingUsers = {};
    this.usersWithError = {};
    this.reminders = {};
    this.pendingReminders = {};
    this.remindersWithError = {};

    // Log
    this.logger.debug('🧹 All cache cleaned.');
  }
}
